import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Modal, Text } from 'rizzui';
import { toast } from 'sonner';
import { Loader } from '../../../components/loader/SpinnerLogo';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { TutorService } from '../../../services/tutor/tutorService';
import { fixNumber } from '../../../utils/formatNumber';

interface Contract {
    id: string;
    code: string;
    totalAmount: string;
}

interface TutorData {
    id: string;
    name: string;
    nifCif: string;
    email: string;
    contracts: Contract[];
}

interface SelectedContract {
    tutorId: string;
    contractId: string;
    tutorName: string;
    contractCode: string;
    totalAmount: string;
}

interface ExternalInvoicingModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    tutorId?: string; // ID del tutor específico para filtrar contratos
}

const ExternalInvoicingModal: React.FC<ExternalInvoicingModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    tutorId
}) => {
    const tutorService = new TutorService();
    const { handleErrors } = useHandleErrors();

    const [selectedContracts, setSelectedContracts] = useState<SelectedContract[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);

    // Fetch invocable contracts
    const [contractsData, contractsLoading, contractsError, refetchContracts] = useFetch(useCallback(async () => {
        const response = tutorId
            ? await tutorService.getInvocableTutorContracts([tutorId])
            : await tutorService.getInvocableTutorContracts();
        return response.getResponseData();
    }, [tutorId]));

    useEffect(() => {
        if (isOpen) {
            setSelectedContracts([]);
        }
    }, [isOpen]);

    // Handle contract selection
    const handleContractSelection = (
        tutorId: string,
        contractId: string,
        tutorName: string,
        contractCode: string,
        totalAmount: string,
        isSelected: boolean
    ) => {
        if (isSelected) {
            setSelectedContracts(prev => [
                ...prev,
                { tutorId, contractId, tutorName, contractCode, totalAmount }
            ]);
        } else {
            setSelectedContracts(prev =>
                prev.filter(contract => contract.contractId !== contractId)
            );
        }
    };

    // Handle tutor selection (select/deselect all contracts for a tutor)
    const handleTutorSelection = (tutorData: TutorData, isSelected: boolean) => {
        if (isSelected) {
            const tutorContracts = tutorData.contracts.map(contract => ({
                tutorId: tutorData.id,
                contractId: contract.id,
                tutorName: tutorData.name,
                contractCode: contract.code,
                totalAmount: contract.totalAmount
            }));
            setSelectedContracts(prev => [
                ...prev.filter(contract => contract.tutorId !== tutorData.id),
                ...tutorContracts
            ]);
        } else {
            setSelectedContracts(prev =>
                prev.filter(contract => contract.tutorId !== tutorData.id)
            );
        }
    };

    // Check if a contract is selected
    const isContractSelected = (contractId: string) => {
        return selectedContracts.some(contract => contract.contractId === contractId);
    };

    // Check if all contracts for a tutor are selected
    const isTutorFullySelected = (tutorData: TutorData) => {
        return tutorData.contracts.every(contract =>
            isContractSelected(contract.id)
        );
    };

    // Check if some contracts for a tutor are selected
    const isTutorPartiallySelected = (tutorData: TutorData) => {
        return tutorData.contracts.some(contract =>
            isContractSelected(contract.id)
        ) && !isTutorFullySelected(tutorData);
    };

    // Generate invoices
    const handleGenerateInvoices = async () => {
        if (selectedContracts.length === 0) {
            toast.error('Debe seleccionar al menos un contrato');
            return;
        }

        setIsGenerating(true);

        try {
            // Group contracts by tutor
            const groupedContracts = selectedContracts.reduce((acc, contract) => {
                const existingTutor = acc.find(item => item.tutorId === contract.tutorId);
                if (existingTutor) {
                    existingTutor.contractIds.push(contract.contractId);
                } else {
                    acc.push({
                        tutorId: contract.tutorId,
                        contractIds: [contract.contractId]
                    });
                }
                return acc;
            }, [] as { tutorId: string; contractIds: string[] }[]);

            const payload = {
                tutorAndContractIds: groupedContracts
            };

            const response = await tutorService.generateTutorInvoices(payload);
            const result = response.getResponseData();

            if (result.success) {
                toast.success('Facturas generadas correctamente');
                refetchContracts();
                onSuccess?.();
                window.dispatchEvent(new CustomEvent('invoicesGenerated'));
                onClose();
            } else {
                handleErrors(response.response);
            }
        } catch (error) {
            console.error('Error generating invoices:', error);
            toast.error('Error al generar las facturas');
        } finally {
            setIsGenerating(false);
        }
    };

    // Calculate totals
    const totalAmount = selectedContracts.reduce((sum, contract) => sum + parseFloat(contract.totalAmount || '0'), 0);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <div className="m-auto px-7 pt-6 pb-8">
                <div className="mb-7 flex items-center justify-between">
                    <Text as="span" className="text-lg font-semibold text-gray-900">
                        Facturar Contratos de Tutores Externos
                    </Text>
                </div>

                {contractsLoading ? (
                    <div className="flex justify-center py-8">
                        <Loader />
                    </div>
                ) : contractsError ? (
                    <div className="text-center py-8">
                        <Text className="text-red-500">Error al cargar los contratos</Text>
                        <Button
                            onClick={refetchContracts}
                            className="mt-4"
                            variant="outline"
                        >
                            Reintentar
                        </Button>
                    </div>
                ) : Object.values(contractsData).length === 0 ? (
                    <div className="text-center py-8">
                        <Text className="text-gray-500">No hay contratos facturables disponibles</Text>
                    </div>
                ) : (
                    <>
                        <div className="max-h-96 overflow-y-auto mb-6">
                            {Object.values(contractsData).map((tutorData: any) => (
                                <div key={tutorData.id} className="mb-6 border rounded-lg p-4">
                                    <div
                                        className="flex items-center mb-3 cursor-pointer hover:bg-gray-50 rounded p-2 -m-2 transition-colors"
                                        onClick={(e) => {
                                            // Detecta si el click viene del checkbox
                                            if ((e.target as HTMLElement).closest('input[type="checkbox"]')) {
                                                return; // No hace nada, deja que el checkbox maneje el evento
                                            }
                                            const shouldSelect = !isTutorFullySelected(tutorData);
                                            handleTutorSelection(tutorData, shouldSelect);
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleTutorSelection(tutorData, !isTutorFullySelected(tutorData));
                                            }
                                        }}
                                        aria-label={`${isTutorFullySelected(tutorData) ? 'Deseleccionar' : 'Seleccionar'} todos los contratos de ${tutorData.name}`}
                                    >
                                        <Checkbox
                                            checked={isTutorFullySelected(tutorData)}
                                            indeterminate={isTutorPartiallySelected(tutorData)}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const shouldSelect = e.target.checked;
                                                handleTutorSelection(tutorData, shouldSelect);
                                            }}
                                            className="mr-3"
                                        />
                                        <div className="flex items-center">
                                            <Text className="font-semibold text-gray-900">
                                                {tutorData.name}
                                            </Text>
                                            <Text className="text-sm text-gray-500 ms-4">
                                                {tutorData.nifCif} • {tutorData.email}
                                            </Text>
                                        </div>
                                    </div>

                                    <div className="ml-8 grid md:grid-cols-2 gap-3">
                                        {tutorData.contracts.map((contract: Contract) => (
                                            <div
                                                key={contract.id}
                                                className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                                                onClick={(e) => {
                                                    // Detecta si el click viene del checkbox
                                                    if ((e.target as HTMLElement).closest('input[type="checkbox"]')) {
                                                        return; // No hace nada, deja que el checkbox maneje el evento
                                                    }
                                                    const shouldSelect = !isContractSelected(contract.id);
                                                    handleContractSelection(
                                                        tutorData.id,
                                                        contract.id,
                                                        tutorData.name,
                                                        contract.code,
                                                        contract.totalAmount,
                                                        shouldSelect
                                                    );
                                                }}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleContractSelection(
                                                            tutorData.id,
                                                            contract.id,
                                                            tutorData.name,
                                                            contract.code,
                                                            contract.totalAmount,
                                                            !isContractSelected(contract.id)
                                                        );
                                                    }
                                                }}
                                                aria-label={`${isContractSelected(contract.id) ? 'Deseleccionar' : 'Seleccionar'} contrato ${contract.code}`}
                                            >
                                                <div className="flex items-center">
                                                    <Checkbox
                                                        checked={isContractSelected(contract.id)}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                            const shouldSelect = e.target.checked;
                                                            handleContractSelection(
                                                                tutorData.id,
                                                                contract.id,
                                                                tutorData.name,
                                                                contract.code,
                                                                contract.totalAmount,
                                                                shouldSelect
                                                            );
                                                        }}
                                                        className="mr-3"
                                                    />
                                                    <div>
                                                        <Text className="font-medium">
                                                            {contract.code}
                                                        </Text>
                                                    </div>
                                                </div>
                                                <Text className="font-semibold text-green-600">
                                                    {fixNumber(parseFloat(contract.totalAmount))}€
                                                </Text>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {selectedContracts.length > 0 && (
                            <div className="bg-primary-50 p-4 rounded-lg mb-6">
                                <div className="flex justify-between items-center">
                                    <Text className="font-medium">
                                        Contratos seleccionados: {selectedContracts.length}
                                    </Text>
                                    <Text className="font-semibold text-primary-600">
                                        Total: {fixNumber(totalAmount)}€
                                    </Text>
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={isGenerating}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleGenerateInvoices}
                        disabled={selectedContracts.length === 0 || isGenerating}
                        isLoading={isGenerating}
                    >
                        {isGenerating ? 'Generando...' : 'Generar Facturas'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ExternalInvoicingModal;